import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useWorkspaceStore } from '../../stores/workspaceStore'
import { governanceApi, type EngagementRules } from '../../services/api'
import {
  Alert,
  Card,
  CardHeader,
  ErrorState,
  Field,
  FieldGrid,
  FieldLabel,
  Hint,
  LoadingState,
  SaveBar,
  Stack,
  TileGrid,
  Toggle,
} from '../../ds'
import { quietWindowProblem } from '@marva/shared/quiet-window'
import { ARCHETYPE_NAMES, getEffectiveConfig } from '@marva/shared/product-categories'
import { matchesStored } from './storedValue'

interface EngagementRulesSectionProps {
  onClose: () => void
}

const DEFAULTS: EngagementRules = {
  timezone: null,
  quietHours: {
    global: { start: 21, end: 8 },
    overrides: { email: null },
  },
  frequencyCaps: {
    email: { perWeek: null, perMonth: null },
    sms: { perMonth: null },
    onSite: { perSession: 3 },
  },
  cooldown: { minGapHours: null },
  fatigue: {
    enabled: true,
    perChannel: { email: 3, sms: 2, onSite: 5 },
    lookbackDays: 30,
  },
  blackoutDates: [],
  boostDates: [],
}

/**
 * Nullable integer field. Empty stores `null`, and for the three archetype-seeded caps the caller
 * passes the store type's default as `placeholder` — the number the enforcer applies to a `null`
 * (`resolveEffectiveCaps` → `getEffectiveConfig(storeDomain.archetype).frequency`), shown in grey.
 * Fields with no seeded default (fatigue) pass none.
 */
const CapField = ({
  label,
  value,
  onChange,
  min = 1,
  max = 100,
  placeholder,
  hint,
}: {
  label: string
  value: number | null
  onChange: (v: number | null) => void
  min?: number
  max?: number
  placeholder?: string
  hint?: string
}) => (
  <Field
    label={label}
    hint={hint}
    type="number"
    min={min}
    max={max}
    value={value ?? ''}
    placeholder={placeholder}
    onChange={(e) => {
      const raw = (e.target as HTMLInputElement).value
      onChange(raw === '' ? null : parseInt(raw, 10))
    }}
  />
)

const HourField = ({ label, value, onChange }: { label: string; value: number; onChange: (v: number) => void }) => (
  <Field
    label={label}
    type="number"
    min={0}
    max={23}
    value={value}
    onChange={(e) => onChange(parseInt((e.target as HTMLInputElement).value, 10))}
  />
)

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/

function parseDates(text: string): { dates: string[]; invalid: boolean } {
  const parts = text
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
  const invalid = parts.some((p) => !DATE_RE.test(p))
  return { dates: parts, invalid }
}

/**
 * Everything the form shows, stamped with the workspace it was read for.
 *
 * ⚠️ Scoped at the READ, never cleared in an effect. React renders once with the new
 * `workspaceId` and the old state before any effect runs, and that render paints workspace A's
 * caps under workspace B's name — a frame in which Save would write A's rules onto B.
 * `AuthorityPanel` carries the same shape for the same reason (PR #103).
 *
 * `rules: null` with `failed: true` means "we could not find out" — never "these are your
 * rules". A successful read carrying no stored row is NOT a failure: that workspace genuinely
 * has no rules yet, and DEFAULTS are the honest thing to show it.
 */
interface RulesSnapshot {
  workspaceId: string
  failed: boolean
  rules: EngagementRules | null
  blackoutText: string
  boostText: string
  /**
   * The rules the STORE holds: the raw row as read (`null` when there is none yet), or what the
   * last successful save sent. Save is disabled while the payload it would send matches this
   * (D-27, `storedValue.ts`) — so a workspace with no row, which is shown DEFAULTS, has
   * something to save, and an edit reverted to the stored value has nothing.
   */
  stored: EngagementRules | null
}

/**
 * The exact object Save PUTs. `handleSave` sends it and the dirty check compares it against
 * `RulesSnapshot.stored`, so the two cannot disagree.
 */
const buildEngagementPayload = (rules: EngagementRules, blackoutText: string, boostText: string): EngagementRules => ({
  ...rules,
  blackoutDates: parseDates(blackoutText).dates,
  boostDates: parseDates(boostText).dates,
})

const EngagementRulesSection = ({ onClose }: EngagementRulesSectionProps) => {
  void onClose
  const currentWorkspace = useWorkspaceStore((s) => s.currentWorkspace)
  const workspaceId = currentWorkspace?.id
  const [snapshot, setSnapshot] = useState<RulesSnapshot | null>(null)
  /**
   * The workspace a save is in flight for — a workspace id, not a boolean.
   *
   * ⚠️ A bare boolean bleeds. Start a save on A, switch to B while it is in flight, and B's
   * Save button reads "Saving…" and is disabled although nothing is being saved for B — it
   * only frees when A's request settles. `snapshot` and `feedback` are both stamped for the
   * same reason, and the SMS cards on this tab carry the identical shape.
   */
  const [saving, setSaving] = useState<string | null>(null)
  const [feedback, setFeedback] = useState<{ workspaceId: string; text: string } | null>(null)

  /** Only the newest read for a workspace may write, and only while it is still on screen. */
  const requestSeq = useRef<Record<string, number>>({})

  const load = useCallback(async(asked: string) => {
    const seq = (requestSeq.current[asked] = (requestSeq.current[asked] ?? 0) + 1)
    const apply = (snap: RulesSnapshot) => {
      if (requestSeq.current[asked] !== seq) return
      // A response for a workspace the user has left must not touch the shared slot at all —
      // not even with correctly-scoped data, because it evicts what the current one put there.
      if (useWorkspaceStore.getState().currentWorkspace?.id !== asked) return
      setSnapshot(snap)
    }
    const unread: RulesSnapshot = {
      workspaceId: asked, failed: true, rules: null, blackoutText: '', boostText: '', stored: null,
    }
    try {
      const res = await governanceApi.getEngagement(asked)
      // ⚠️ A refusal RESOLVES, and THIS branch is the one that runs. `apiClient.request`
      // (`services/api.ts:129`) wraps its whole body in try/catch and returns
      // `{ success: false, error: 'Network error' }` — it never rejects. So a network failure,
      // a 500 and a 403 all arrive here, and the `catch` below is unreachable through this
      // client. It stays as belt-and-braces for a non-apiClient caller; it is not what covers
      // a failed read. Do not delete this branch on the strength of that catch.
      //
      // The defect: `if (res.success && res.data)` with no `else` left DEFAULTS on screen
      // looking like stored settings, and `handleSave` PUTs the whole object — so one click
      // replaced the merchant's real rules with the defaults they had been shown.
      if (!res.success) { apply(unread); return }
      // …but an empty body is not one of them. No row means no rules yet, which is a fact,
      // not an outage — rendering the failure state here would turn every brand-new
      // workspace into a permanent error screen.
      const merged: EngagementRules = { ...DEFAULTS, ...(res.data ?? {}) }
      const blackout = (merged.blackoutDates || []).join(', ')
      const boost = (merged.boostDates || []).join(', ')
      apply({
        workspaceId: asked,
        failed: false,
        rules: merged,
        blackoutText: blackout,
        boostText: boost,
        stored: res.data ?? null,
      })
    } catch {
      apply(unread)
    }
  }, [])

  useEffect(() => {
    if (!workspaceId) return
    void load(workspaceId)
  }, [load, workspaceId])

  // ── Scoped at the read. A value belonging to another workspace is simply not current. ──
  const current = snapshot?.workspaceId === workspaceId ? snapshot : null
  const rules = current && !current.failed ? current.rules : null
  const blackoutText = current?.blackoutText ?? ''
  const boostText = current?.boostText ?? ''
  const message = feedback && feedback.workspaceId === workspaceId ? feedback.text : null
  const isUpdating = saving === workspaceId

  const setRules = (update: (r: EngagementRules) => EngagementRules) =>
    setSnapshot((s) => (s && s.rules ? { ...s, rules: update(s.rules) } : s))
  const setBlackoutText = (text: string) => setSnapshot((s) => (s ? { ...s, blackoutText: text } : s))
  const setBoostText = (text: string) => setSnapshot((s) => (s ? { ...s, boostText: text } : s))

  /** Drop back to the loading state and read again — the only way out of a failed read. */
  const retry = () => {
    if (!workspaceId) return
    setSnapshot(null)
    setFeedback(null)
    void load(workspaceId)
  }

  // Validate before hitting the API (guardrail-config inputs must not send out-of-range/incomplete
  // values — see the known web-app P5 bug). Returns an error string, or null if valid.
  const validate = (r: EngagementRules): string | null => {
    const inRange = (h: number) => Number.isInteger(h) && h >= 0 && h <= 23
    if (!inRange(r.quietHours.global.start) || !inRange(r.quietHours.global.end)) {
      return 'Quiet-hours times must be whole hours between 0 and 23.'
    }
    // MAR-163: the same rule `EngagementRulesSchema` refuses with — never a second copy of it.
    const quiet = quietWindowProblem(r.quietHours.global)
    if (quiet) return quiet.message
    const caps = [
      r.frequencyCaps.email.perWeek,
      r.frequencyCaps.email.perMonth,
      r.frequencyCaps.sms.perMonth,
      r.cooldown.minGapHours,
    ]
    if (caps.some((c) => c !== null && (!Number.isInteger(c) || (c as number) < 0))) {
      return 'Frequency caps and cool-down must be whole non-negative numbers (leave blank for the automatic default).'
    }
    const badBlackout = parseDates(blackoutText).invalid
    const badBoost = parseDates(boostText).invalid
    if (badBlackout || badBoost) {
      return 'Dates must be in YYYY-MM-DD format, comma-separated.'
    }
    return null
  }

  const handleSave = async() => {
    // ⚠️ Belt and braces. The Save control is not rendered for a failed read, and no save can
    // be built without rules the server actually returned — the defect this guards is a
    // whole-object PUT assembled from DEFAULTS the merchant never chose.
    if (!workspaceId || !current || !rules) return
    const asked = workspaceId
    const err = validate(rules)
    if (err) {
      setFeedback({ workspaceId: asked, text: err })
      return
    }
    // The exact form state this payload is built from. Every mutator replaces the snapshot
    // object, so identity is what tells us afterwards whether the merchant typed during the
    // request — see the reconcile below.
    const sentFrom = current
    try {
      setSaving(asked)
      setFeedback(null)
      const payload = buildEngagementPayload(rules, blackoutText, boostText)
      const res = await governanceApi.saveEngagement(asked, payload)
      if (!res.success) throw new Error(res.error || 'Failed to save engagement rules')
      // Reconcile, don't overwrite. This step exists only to normalise the two date arrays to
      // what the text boxes actually sent, so it may only land on the form it was built from.
      // ⚠️ The fields stay editable during the request on purpose — a merchant who cannot type
      // is a worse surface than one whose newest keystroke outlives the save. So if anything
      // changed since the click, that edit is the newer intent and stands; blowing it away
      // under "Engagement rules saved." is the defect. Identity of the snapshot covers every
      // field, and implies the workspace is still `asked`.
      //
      // Either way what is stored becomes what was SENT, so an edit made during the request is
      // still an unsaved change afterwards.
      setSnapshot((s) =>
        s === sentFrom
          ? { ...s, rules: payload, stored: payload }
          : s && s.workspaceId === asked
            ? { ...s, stored: payload }
            : s
      )
      setFeedback({ workspaceId: asked, text: 'Engagement rules saved.' })
    } catch (e) {
      setFeedback({
        workspaceId: asked,
        text: e instanceof Error ? e.message : 'Failed to save engagement rules',
      })
    } finally {
      setSaving((s) => (s === asked ? null : s))
    }
  }

  // Three states, kept apart the way the shell keeps them (`useNeedsYou`): still reading ·
  // could not read · read fine. "Nothing set yet" belongs to the third — it arrives as a
  // successful read and renders the defaults below.
  if (!current) return <LoadingState caption="Loading engagement rules…" />

  if (!rules) {
    // Honest unknown. Showing the form here is the defect: every field would read DEFAULTS,
    // and Save PUTs the whole object, so one click would overwrite real stored rules with
    // values the merchant never typed. So there is no form, and nothing to save.
    return (
      <ErrorState title="Your engagement rules could not be read." onRetry={retry} retryLabel="Try again">
        <p>
          Nothing is shown rather than a guess — a form filled with defaults would save over the limits you
          actually set.
        </p>
        <p>
          Marva is still applying the rules she has stored: she reads them herself each time she
          messages someone. This is this screen failing to read them, not the rules lapsing.
        </p>
      </ErrorState>
    )
  }

  const q = rules.quietHours.global
  const fc = rules.frequencyCaps
  const fat = rules.fatigue

  const isError = message != null && !message.includes('saved')
  const dirty = !matchesStored(buildEngagementPayload(rules, blackoutText, boostText), current.stored)

  // The store type the enforcer seeds a blank cap from — the same key, the same fallback
  // (`getEffectiveConfig` falls back to General Retail for an absent or unknown archetype).
  const archetype = currentWorkspace?.storeDomain?.archetype
  const seeded = getEffectiveConfig(archetype).frequency
  const storeTypeName = ARCHETYPE_NAMES[archetype && ARCHETYPE_NAMES[archetype] ? archetype : 'general_retail']

  return (
    <Card>
      <CardHeader
        titleAs="h2"
        title="How often she writes"
        description="How often and when Marva emails and texts your customers. A text's quiet hours and monthly cap are set in Text messages."
      />
      {/* MAR-157: texts now pass the same send-time enforcer as email (`governOutboundSend` in
          the gateway), so cool-down, fatigue (including the SMS field below) and blackout dates
          apply to both. Two things a text does NOT take from this card, and the sentence says
          so: its quiet hours (`smsConfig` window ∪ the TCPA floor, enforced in
          `checkCanSendSms`) and its monthly cap (`smsConfig.maxSmsPerCustomerMonth`) — both
          in Text messages. On-site chat is governed by none of these, by design. */}

      <Stack gap="sm">
        <FieldLabel as="h3">Quiet hours · email</FieldLabel>
        <Hint>When Marva won&apos;t message a customer, in their local time.</Hint>
        <FieldGrid>
          <HourField
            label="Quiet from (hour, 24h)"
            value={q.start}
            onChange={(v) => setRules((r) => ({ ...r, quietHours: { ...r.quietHours, global: { ...r.quietHours.global, start: v } } }))}
          />
          <HourField
            label="Quiet until (hour, 24h)"
            value={q.end}
            onChange={(v) => setRules((r) => ({ ...r, quietHours: { ...r.quietHours, global: { ...r.quietHours.global, end: v } } }))}
          />
        </FieldGrid>
        {/* ⚠️ This used to promise "SMS always respects the legal 9pm–8am minimum, even if you set
            a narrower window here." Both halves were false, and MAR-154 fixed only one of them.
            An SMS's quiet hours read `workspace.smsConfig`, never these rules, so the sentence
            stays deleted until this field is what a text obeys. */}
        {/* MAR-163: said while the merchant is choosing, not only when Save refuses it. */}
        {quietWindowProblem(q) ? (
          <Alert tone="danger" live="assertive">
            Starting and ending at the same hour covers the whole day — Marva would never send another
            email. Pick two different hours.
          </Alert>
        ) : null}
      </Stack>

      <Stack gap="sm">
        <FieldLabel as="h3">Frequency caps</FieldLabel>
        <Hint>
          The most messages a single customer receives. Leave blank to use the default for your store type — shown in grey.
        </Hint>
        <TileGrid>
          <CapField label="Emails per customer / week" placeholder={String(seeded.emailPerWeek)} value={fc.email.perWeek} onChange={(v) => setRules((r) => ({ ...r, frequencyCaps: { ...r.frequencyCaps, email: { ...r.frequencyCaps.email, perWeek: v } } }))} />
          <CapField label="Emails per customer / month" placeholder={String(seeded.emailPerMonth)} value={fc.email.perMonth} onChange={(v) => setRules((r) => ({ ...r, frequencyCaps: { ...r.frequencyCaps, email: { ...r.frequencyCaps.email, perMonth: v } } }))} />
          <CapField label="Minimum gap (hours)" hint="Between any two messages to the same customer." placeholder={String(seeded.minGapHours)} value={rules.cooldown.minGapHours} min={0} max={336} onChange={(v) => setRules((r) => ({ ...r, cooldown: { minGapHours: v } }))} />
        </TileGrid>
        {/* ⚠️ Two caps are deliberately absent here, for the same reason: neither binds a send.
            `frequencyCaps.sms.perMonth` binds only when the SMS config carries no stored cap
            (precedence `smsConfig.maxSmsPerCustomerMonth` → this rule → a flat 6), so rendering it
            would show two SMS caps whose winner the merchant cannot see; and
            `frequencyCaps.onSite.perSession` has no call site at all. Both still round-trip
            untouched: the save sends the whole rules object back. Do not re-add either. */}
        <Hint>
          Defaults for <Link to="/authority?tab=store&section=type">{storeTypeName}</Link> stores.
        </Hint>
      </Stack>

      <Stack gap="sm">
        <Toggle
          checked={fat.enabled}
          label="Enable fatigue reduction"
          description="Automatically ease off a channel a customer keeps ignoring."
          onChange={() => setRules((r) => ({ ...r, fatigue: { ...r.fatigue, enabled: !r.fatigue.enabled } }))}
        />
        {fat.enabled && (
          <TileGrid>
            <CapField label="Ease off email after N ignored" value={fat.perChannel.email} onChange={(v) => setRules((r) => ({ ...r, fatigue: { ...r.fatigue, perChannel: { ...r.fatigue.perChannel, email: v ?? 3 } } }))} />
            <CapField label="Ease off SMS after N ignored" value={fat.perChannel.sms} onChange={(v) => setRules((r) => ({ ...r, fatigue: { ...r.fatigue, perChannel: { ...r.fatigue.perChannel, sms: v ?? 2 } } }))} />
            <CapField label="Ease off on-site after N ignored" value={fat.perChannel.onSite} onChange={(v) => setRules((r) => ({ ...r, fatigue: { ...r.fatigue, perChannel: { ...r.fatigue.perChannel, onSite: v ?? 5 } } }))} />
          </TileGrid>
        )}
      </Stack>

      <Stack gap="sm">
        <FieldLabel as="h3">Special dates</FieldLabel>
        <FieldGrid>
          <Field label="Blackout dates" hint="No marketing. YYYY-MM-DD, comma-separated." placeholder="2026-12-25, 2027-01-01" value={blackoutText} onChange={(e) => setBlackoutText((e.target as HTMLInputElement).value)} />
          <Field label="Boost dates" hint="Higher limits. YYYY-MM-DD, comma-separated." placeholder="2026-11-29" value={boostText} onChange={(e) => setBoostText((e.target as HTMLInputElement).value)} />
        </FieldGrid>
      </Stack>

      {message && (
        <Alert tone={isError ? 'danger' : 'success'} live={isError ? 'assertive' : 'polite'}>
          {message}
        </Alert>
      )}

      <SaveBar
        placement="footer"
        state={isUpdating ? 'saving' : dirty ? 'dirty' : 'clean'}
        note="Changes apply from her next message."
        saveLabel="Save engagement rules"
        onSave={handleSave}
      />
    </Card>
  )
}

export default EngagementRulesSection
